import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, ArrowLeft, Check, X } from "lucide-react";
import { SpurwechselLogo, SpurwechselIcon } from "@/components/SpurwechselLogo";
import { ARTICLES, ARTICLE_CATEGORIES } from "@/lib/seo-content";

export default function SEOArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = ARTICLES[slug];

  // Dynamic page title for SEO
  if (article) {
    document.title = `${article.title} | Spurwechsel`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", article.meta);
  }

  if (!article) {
    // Show article index if no slug
    return (
      <div className="min-h-screen bg-background" data-testid="seo-index">
        <nav className="glass-nav sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2"><SpurwechselLogo size="small" /></Link>
            <Link to="/register"><Button className="font-body text-sm bg-primary text-primary-foreground">Kostenlos starten</Button></Link>
          </div>
        </nav>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-8">MPU-Wissen — Alle Ratgeber</h1>
          {Object.entries(ARTICLE_CATEGORIES).map(([catKey, cat]) => (
            <div key={catKey} className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4">{cat.label}</h2>
              <div className="space-y-2">
                {cat.slugs.map(s => ARTICLES[s] && (
                  <Link key={s} to={`/wissen/${s}`} className="block p-4 rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-sm transition-all">
                    <p className="font-body text-base font-medium text-foreground">{ARTICLES[s].title}</p>
                    <p className="font-body text-sm text-muted-foreground mt-1">{ARTICLES[s].meta}</p>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="seo-article">
      <nav className="glass-nav sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2"><SpurwechselLogo size="small" /></Link>
          <Link to="/register"><Button className="font-body text-sm bg-primary text-primary-foreground">Kostenlos starten</Button></Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/wissen" className="inline-flex items-center gap-1 font-body text-sm text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Alle Ratgeber
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-8">{article.title}</h1>

        <div className="space-y-10">
          {article.sections.map((sec, i) => (
            <section key={i}>
              <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-3">{sec.h2}</h2>
              {sec.content && <p className="font-body text-base text-muted-foreground leading-relaxed">{sec.content}</p>}
              {sec.items && (
                <ul className="space-y-2 mt-3">{sec.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                    <span className="font-body text-base text-muted-foreground leading-relaxed">{item}</span>
                  </li>
                ))}</ul>
              )}
              {/* Beispiel-Antworten (Gut vs. Schlecht) */}
              {sec.example && (
                <div className="mt-4 space-y-3">
                  <p className="font-body text-sm font-medium text-foreground italic">Frage: "{sec.example.question}"</p>
                  <div className="rounded-xl border-l-4 border-l-success bg-success/5 p-4">
                    <div className="flex items-center gap-1.5 mb-2"><Check className="w-4 h-4 text-success" /><span className="font-body text-sm font-semibold text-success">Reflektierte Antwort</span></div>
                    <p className="font-body text-sm text-muted-foreground italic leading-relaxed">"{sec.example.good}"</p>
                  </div>
                  <div className="rounded-xl border-l-4 border-l-error bg-error/5 p-4">
                    <div className="flex items-center gap-1.5 mb-2"><X className="w-4 h-4 text-error" /><span className="font-body text-sm font-semibold text-error">Problematische Antwort</span></div>
                    <p className="font-body text-sm text-muted-foreground italic leading-relaxed">"{sec.example.bad}"</p>
                  </div>
                  <p className="font-body text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">{sec.example.analysis}</p>
                </div>
              )}
            </section>
          ))}
        </div>

        {/* FAQ */}
        {article.faq?.length > 0 && (
          <section className="mt-12 border-t border-border pt-8">
            <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-6">Häufig gestellte Fragen</h2>
            <div className="space-y-4">{article.faq.map((item, i) => (
              <Card key={i} className="border-border/50"><CardContent className="pt-4 pb-3">
                <h3 className="font-body text-base font-semibold mb-2">{item.q}</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{item.a}</p>
              </CardContent></Card>
            ))}</div>
          </section>
        )}

        {/* Related Articles */}
        {article.related?.length > 0 && (
          <section className="mt-10 border-t border-border pt-8">
            <h2 className="font-body text-sm font-medium text-muted-foreground mb-4">Weiterlesen</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {article.related.map(r => ARTICLES[r] && (
                <Link key={r} to={`/wissen/${r}`} className="p-3 rounded-xl border border-border/50 hover:border-primary/30 hover:shadow-sm transition-all group">
                  <p className="font-body text-sm font-medium group-hover:text-primary">{ARTICLES[r].title}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <Card className="mt-10 border-primary/20 bg-primary/5">
          <CardContent className="pt-6 pb-5 text-center">
            <h3 className="font-heading text-xl font-bold mb-2">Gespräch üben statt nur lesen</h3>
            <p className="font-body text-sm text-muted-foreground mb-4">Trainieren Sie den psychologischen Teil der MPU mit KI-gestützten Übungsgesprächen.</p>
            <Button className="font-body bg-primary text-primary-foreground" onClick={() => navigate("/register")}>
              Kostenlos starten <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      </article>

      <footer className="border-t border-border/50 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="font-body text-xs text-muted-foreground">Spurwechsel ist ein Übungswerkzeug und ersetzt keine professionelle MPU-Beratung, Therapie oder Rechtsberatung.</p>
        </div>
      </footer>

      {/* FAQ Schema.org */}
      {article.faq && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": article.faq.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      })}} />}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "Article",
        "headline": article.title, "description": article.meta,
        "publisher": { "@type": "Organization", "name": "Spurwechsel" }
      })}} />
    </div>
  );
}
